/**
 * Cookie consent utility for GDPR compliance
 * Shared across components to check and manage cookie consent
 *
 * GA4 is loaded on every page via the Consent Mode v2 snippet in index.html.
 * Defaults are region-scoped: analytics/ad storage is denied by default only
 * in EEA/UK/CH (where opt-in consent is required) and granted elsewhere.
 * This module only flips the consent state via gtag('consent', 'update', ...).
 */

const CONSENT_STORAGE_KEY = 'wage_atlas_cookie_consent';

const updateGtagConsent = (granted: boolean) => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;

  const value = granted ? 'granted' : 'denied';
  window.gtag('consent', 'update', {
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
    analytics_storage: value,
  });
};

export const cookieConsent = {
  /**
   * Check if user has given consent for cookies
   */
  hasConsent: (): boolean => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(CONSENT_STORAGE_KEY) === 'accepted';
  },

  /**
   * Apply previously stored consent on app boot. Must run early so the
   * consent update lands within gtag's wait_for_update window.
   */
  applyStoredConsent: () => {
    if (typeof window === 'undefined') return;
    if (localStorage.getItem(CONSENT_STORAGE_KEY) === 'accepted') {
      updateGtagConsent(true);
    }
  },

  /**
   * Set cookie consent preference
   * Dispatches a custom event when consent is given to allow components to react
   */
  setConsent: (accepted: boolean) => {
    if (typeof window === 'undefined') return;

    localStorage.setItem(CONSENT_STORAGE_KEY, accepted ? 'accepted' : 'rejected');
    updateGtagConsent(accepted);

    window.dispatchEvent(
      new CustomEvent(accepted ? 'cookieConsentAccepted' : 'cookieConsentRejected')
    );
  },

  /**
   * Check if consent banner should be shown
   */
  showConsentBanner: (): boolean => {
    if (typeof window === 'undefined') return false;
    return !localStorage.getItem(CONSENT_STORAGE_KEY);
  },
};
